import React, { useState, useEffect, useRef } from "react";
import { useMessaging } from "../../context/MessagingContext";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "../../context/UserRoleContext";

const BarContactsDropdown = ({
  isOpen,
  onClose,
  barId,
  buttonRef,
  excludeUserIds,
}) => {
  const { getOrCreateConversation } = useMessaging();
  const { userData } = useUserRole();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  // Calculate dropdown position based on button position
  useEffect(() => {
    if (isOpen && buttonRef?.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 350;
      const dropdownHeight = 400;

      // Calculate right position (distance from right edge)
      let right = window.innerWidth - buttonRect.right;

      // If dropdown would overflow on the right, align to button's right edge
      if (buttonRect.right + dropdownWidth > window.innerWidth) {
        right = window.innerWidth - buttonRect.right;
      }

      // If dropdown would overflow on the left, adjust
      if (right + dropdownWidth > window.innerWidth) {
        right = Math.max(8, window.innerWidth - dropdownWidth - 8);
      }

      // Calculate top position
      let top = buttonRect.bottom + 8;

      // If dropdown would overflow on the bottom, show above button
      if (top + dropdownHeight > window.innerHeight) {
        top = buttonRect.top - dropdownHeight - 8;
        // If still doesn't fit, position at top of viewport
        if (top < 0) {
          top = 8;
        }
      }

      setPosition({ top, right });
    }
  }, [isOpen, buttonRef]);

  // Fetch contacts when dropdown opens
  useEffect(() => {
    if (isOpen && barId) {
      fetchContacts();
    } else {
      setContacts([]);
      setError(null);
    }
  }, [isOpen, barId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef?.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose, buttonRef]);

  const fetchContacts = async () => {
    if (!barId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/bars/${barId}/contacts`, {
        credentials: "include",
      });
      const data = await response.json();

      if (data.success) {
        const contactsList = (data.contacts || []).filter(
          (c) => !excludeUserIds?.includes(c.user_id)
        );
        setContacts(contactsList);
      } else {
        setError(data.error || "Failed to load contacts");
      }
    } catch (err) {
      console.error("Error fetching contacts:", err);
      setError(err.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContact = async (contact) => {
    try {
      setLoading(true);
      const conversation = await getOrCreateConversation(contact.user_id);
      if (conversation) {
        onClose();
        // Navigate to messages page with conversation ID
        const basePath =
          userData?.role === "artist"
            ? "/artist"
            : userData?.role === "bar_owner"
            ? "/bar-owner"
            : "/bar-admin";
        navigate(
          `${basePath}/messages?conversation=${conversation.conversation_id}`
        );
      }
    } catch (err) {
      console.error("Error creating conversation:", err);
      setError(err.message || "Failed to start conversation");
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "bar_owner":
        return <span className="recipient-role-badge owner">Owner</span>;
      case "bar_admin":
        return <span className="recipient-role-badge admin">Admin</span>;
      default:
        return null;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="bar-contacts-dropdown"
      ref={dropdownRef}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        right: `${position.right}px`,
        zIndex: 2000,
      }}
    >
      <div className="new-message-dropdown-header">
        <h3>Message</h3>
        <button
          type="button"
          className="new-message-dropdown-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="new-message-dropdown-list">
        {loading && contacts.length === 0 ? (
          <div className="new-message-loading">
            <div className="spinner"></div>
            <p>Loading contacts...</p>
          </div>
        ) : error ? (
          <div className="new-message-error">
            <p>{error}</p>
            <button
              type="button"
              onClick={fetchContacts}
              className="new-message-retry-btn"
            >
              Retry
            </button>
          </div>
        ) : contacts.length === 0 ? (
          <div className="new-message-empty">
            <p>No contacts available</p>
          </div>
        ) : (
          contacts.map((contact) => (
            <div
              key={contact.user_id}
              className="new-message-recipient-item"
              onClick={() => handleSelectContact(contact)}
            >
              <div className="recipient-avatar">
                {contact.name?.charAt(0).toUpperCase() || "?"}
              </div>
              <div className="recipient-info">
                <div className="recipient-name-row">
                  <span className="recipient-name">{contact.name}</span>
                  {getRoleBadge(contact.role)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BarContactsDropdown;
